import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";
import PageHeader from "../../Component/PageHeader";
import Pill from "../../Component/Pill";
import { CLIENTS, DemoTicket, PRIORITY_TONE, QUALITY_COLOR, SUPPORT_AGENTS, TICKETS, inr } from "../../utils/staticData";

// STATIC DESIGN — tickets live in local state until the support API exists
type StatusTab = "open" | "pending" | "resolved";

const Tickets = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState<DemoTicket[]>(TICKETS);
  const [statusTab, setStatusTab] = useState<StatusTab>("open");
  const [agent, setAgent] = useState("all");
  const [selId, setSelId] = useState(TICKETS[0].id);
  const [mode, setMode] = useState<"reply" | "note">("reply");
  const [draft, setDraft] = useState("");

  const count = (s: StatusTab) => tickets.filter(t => t.st === s).length;
  const list = tickets.filter(t =>
    t.st === statusTab &&
    (agent === "all" || (agent === "unassigned" ? t.who === "Unassigned" : t.who === agent))
  );
  const sel = tickets.find(t => t.id === selId) || list[0];
  const client = sel ? CLIENTS.find(c => c.name === sel.c) : undefined;

  const update = (id: string, patch: Partial<DemoTicket>) =>
    setTickets(ts => ts.map(t => (t.id === id ? { ...t, ...patch } : t)));

  const send = () => {
    if (!sel || !draft.trim()) return;
    const msg: [string, string, string] = [mode === "note" ? "n" : "m", draft.trim(), mode === "note" ? `Admin · ${moment().format("HH:mm")}` : moment().format("HH:mm")];
    update(sel.id, { msgs: [...sel.msgs, msg] });
    setDraft("");
    toast.success(mode === "note" ? "Internal note added" : "Reply sent");
  };

  const resolve = () => {
    if (!sel) return;
    update(sel.id, { st: "resolved" });
    toast.success(`${sel.id} resolved`);
  };

  return (
    <div className="container-fluid">
      <div className="cx">
        <PageHeader
          crumb="Tickets"
          title="Support tickets"
          sub="All tickets raised from the client app's “Raise ticket”."
          actions={
            <select className="cx-select" style={{ height: 38 }} value={agent} onChange={(e) => setAgent(e.target.value)}>
              <option value="all">All agents</option>
              <option value="unassigned">Unassigned</option>
              {SUPPORT_AGENTS.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          }
        />

        <div className="cx-card">
          <div className="cx-tk-layout">
            <div className="cx-tk-list">
              <div className="cx-toolbar">
                <div className="cx-chips">
                  {(["open", "pending", "resolved"] as StatusTab[]).map(s => (
                    <button type="button" key={s} className={`cx-chip ${statusTab === s ? "on" : ""}`} onClick={() => setStatusTab(s)}>
                      {s[0].toUpperCase() + s.slice(1)} <b>{count(s)}</b>
                    </button>
                  ))}
                </div>
              </div>
              {list.length ? list.map(t => (
                <div key={t.id} className={`cx-tk ${sel && t.id === sel.id ? "on" : ""}`} onClick={() => setSelId(t.id)}>
                  <div className="l1"><span className="mono">{t.id} · {t.c}</span><span>{t.age}</span></div>
                  <div className="l2">{t.sub}</div>
                  <div className="l3"><Pill tone={PRIORITY_TONE[t.pri]}>{t.pri}</Pill> &nbsp;{t.cat} · {t.who}</div>
                </div>
              )) : <div className="cx-empty"><i className="ri-inbox-line"></i>No {statusTab} tickets.</div>}
            </div>

            <div className="cx-tk-pane">
              {sel && list.some(t => t.id === sel.id) ? (
                <>
                  <div className="cx-card-h">
                    <div style={{ minWidth: 0 }}>
                      <h6>{sel.sub}</h6>
                      <div className="fs12 muted">
                        <span className="mono">{sel.id}</span> · {sel.c}
                        {client && <> · {client.plan} · wallet <span className="mono">{inr(client.wallet)}</span> · <span className="cx-qdot" style={{ background: QUALITY_COLOR[client.q] }}></span><span className="mono">{client.num}</span></>}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      <select className="cx-select" style={{ height: 32, fontSize: 12.5 }} value={sel.who} onChange={(e) => { update(sel.id, { who: e.target.value }); toast.success(`Assigned to ${e.target.value}`); }}>
                        {["Unassigned", ...SUPPORT_AGENTS].map(a => <option key={a} value={a}>{a}</option>)}
                      </select>
                      <button type="button" className="cx-btn cx-btn-light cx-btn-xs" onClick={() => navigate(`/customers-and-users?search=${encodeURIComponent(sel.c)}`)}>Client</button>
                      {sel.st !== "resolved" && <button type="button" className="cx-btn cx-btn-signal cx-btn-xs" onClick={resolve}>Resolve</button>}
                    </div>
                  </div>
                  <div className="cx-tk-msgs">
                    {sel.msgs.map(([kind, text, time], i) => (
                      <div key={i} className={`cx-msg ${kind === "m" ? "me" : kind === "n" ? "note" : ""}`}>
                        {kind === "n" && <b>Internal note · </b>}{text}<small>{time}</small>
                      </div>
                    ))}
                  </div>
                  <div className="cx-tk-reply">
                    <div className="cx-seg">
                      <button type="button" className={mode === "reply" ? "on" : ""} onClick={() => setMode("reply")}>Reply</button>
                      <button type="button" className={mode === "note" ? "on" : ""} onClick={() => setMode("note")}>Note</button>
                    </div>
                    <input
                      className="cx-input"
                      placeholder={mode === "note" ? "Internal note — not visible to the client…" : "Type a reply…"}
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") send(); }}
                    />
                    <button type="button" className="cx-btn cx-btn-primary" onClick={send} disabled={!draft.trim()} aria-label="Send"><i className="ri-send-plane-2-fill"></i></button>
                  </div>
                </>
              ) : (
                <div className="cx-empty" style={{ margin: "auto" }}><i className="ri-chat-3-line"></i>Select a ticket</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Tickets;
